
import type { StateCreator } from 'zustand';
import { clearData, retrieveData, storeData, type StoreState } from '..';

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import AutomaticSpeechRecognition from 'src/shared/utils/transcription-worker';
import QcastApi from './api';


interface IndexedVideo {
  index: number;
  video: any;
  duration: number,
  transcriptionText?: string;
  editedBlob?: any;
  thumbnail?: any;
}
export interface QcastSlice {
  questions: any;
  transcription:any[];
  thumbnails: string;
  qcastView:string;
  videoTranscription:Record<number, string>[];
  questionIndex: number;
  transcriptionComplete: boolean;
  responses: any[];
  dbResponse: any;
  ffmpegInstance: any;
  transcriber: any;
  recordedVideo: IndexedVideo[],
  qcastLoading:boolean,
  responseVideo:any[],
  updateRecordedVideo: (index: number, options?: { transcriptionText?: string; editedBlob?: Blob, thumbnail?: any,positions?:{start:number,end:number} })=>void,
  addRecordedVideo: (index: number, video: any, duration: number, transcriptionText?: string)=> void,
  setQuestionIndex: (index: number) => void;
  saveQcastResponse: (qcastDetails: any) => Promise<boolean>;
  setThumbnail: (thumb: string) => void;
  setVideoTranscription: (videoTranscription: Record<number, string>) => void;
  setResponses: (responses: any) => void;
  loadQuestions: (qcastId: string, userId?: string) => void;
  setQuestions: (questions: any) => void;
  setResponseVideo:(responseVideo:any[])=>void;
  setQcastView: (qcastView: string) => void;
  setQcastLoading: (qcastLoading: boolean) => void;
  initializeTranscription: (asr: any)=>void;
  saveVideo: (id: string, blob: Blob) => Promise<void>;
  loadVideo: (id: string) => Promise<Blob | null>;
  updateResponderDetails: (detail: any) => Promise<boolean>;
  saveQcast: (qcastDetails: any) => Promise<any>;
  setTranscriptionComplete: (isComplete: boolean) => void;
  clearQcasts: () => void;
}

export const createQcastSlice: StateCreator<
  StoreState,
  [],
  [],
  QcastSlice
> = (set, get) => ({
  questionIndex: 0,
  responseVideo:[],
  qcastLoading:true,
  qcastView:"qcast",
  transcriptionComplete: false,
  transcription: [],
  videoTranscription: [],
  thumbnails: "",
  questions: {},
  responses: [],
  recordedVideo: [],
  ffmpegInstance: null,
  dbResponse: null,
  transcriber: null,
  setQuestionIndex: (index: number) => {
    set(() => ({ questionIndex: index }));
  },
  setResponses: (resp: any) => {
    set((state) => ({
      responses: [...state.responses, resp],
    }));
  },
  addRecordedVideo: async (index: number, video: Blob, duration: number, transcriptionText?: string) => {
    
    await storeData(`${index}`, video);
  
    set((state) => {
      const existingIndex = state.recordedVideo.findIndex((v) => v.index === index);
      
      if (existingIndex !== -1) {
        const updatedVideos = [...state.recordedVideo];
        updatedVideos[existingIndex] = { index, video, duration, transcriptionText };
        return { recordedVideo: updatedVideos };
      }
  
      return { recordedVideo: [...state.recordedVideo, { index, video, duration, transcriptionText }] };
    });
  },
  
  updateRecordedVideo: async (
    index: number,
    options?: { transcriptionText?: string; editedBlob?: Blob, thumbnail?: any,positions?:{start:number,end:number} }
  ) => {
    if (options?.editedBlob) {
      await storeData(`${index}-edited`, options.editedBlob);
    }
    set((state) => {
      const existingIndex = state.recordedVideo.findIndex((v) => v.index === index);
      
      if (existingIndex !== -1) {
        const updatedVideos = [...state.recordedVideo];
        const existingVideo = updatedVideos[index];
  
        updatedVideos[index] = {
          ...existingVideo,
          ...(options?.transcriptionText !== undefined && { transcriptionText: options.transcriptionText }),
          ...(options?.editedBlob !== undefined && { editedBlob: options.editedBlob }),
          ...(options?.thumbnail !== undefined && { thumbnail: options.thumbnail }),
          ...(options?.positions !== undefined && { positions: options.positions }),
        };
        return { recordedVideo: updatedVideos };
      }
  
      return state;
    });
  },
  loadQuestions: async (qcastId: string, userId?: string) => {
    set(() => ({ activityLoader: true }));
    try {
      let response: any;
      if(userId){
        response = await QcastApi.getQcastDeepCopy(qcastId, userId);
      }else{
        response = await QcastApi.getQcastBtId(qcastId);
      }
      
      
      let response2: any
      let responses2: any
      try{ 
        if(qcastId){
          responses2= await QcastApi.getTranscription(qcastId);
        }else{
          responses2=[]
        }
         response2 = responses2.sort((a, b) => a.index - b.index)
      }catch{
        response2=[]
      }
      console.log("response2",response2)
      set(() => ({ questions: response.data, transcription:response2 }));
    } catch (e) {
      set(() => ({ activityLoader: false }));
      throw e;
    } finally {
      set(() => ({ activityLoader: false }));
    }
  },
  setQuestions: (questions: any) => {
    set(() => ({ questions }));
  },setResponseVideo: (responseVideo: any) => {
    set(() => ({ responseVideo:[...responseVideo] }));
  },
  setQcastView: (qcastView: string) => {
    set(() => ({ qcastView }));
  },setQcastLoading: (qcastLoading: boolean) => {
    set(() => ({ qcastLoading }));
  },
  setDBResponse: (response: any) => {
    set(() => ({ dbResponse: response }));
  },
  setVideoTranscription: (newTranscription: any) => {
    set((state) => ({
        videoTranscription: { ...state.videoTranscription, ...newTranscription }
    }));
  },
  setThumbnail: (thumb: string) => {
    set(() => ({ thumbnails: thumb }));
  },
  setTranscriptionComplete: (isComplete) => {
    set({ transcriptionComplete: isComplete });
  },
  initializeTranscription: async (asr: any) => {
    set(() => ({ transcriber: asr }));
  },
  saveVideo: async (id, blob) => {
    await storeData(id, blob);
  },
  saveQcastResponse: async(qcastDetails: any)=>{
    set(()=>({activityLoader: true}));
    try{
      const userDetail = await QcastApi.getUserDetail();
      const details = {
        ...qcastDetails,
        userId: userDetail?.data?.userId,
        thumbnail: qcastDetails?.thumbnails || userDetail?.data?.profilePic
      };

      const response = await QcastApi.saveResponse(details);
      if(response?.id===200){
        await clearData();
        set(() => ({
          response: [],
          recordedVideo: [],
          dbResponse: response.data
        }));
        return true;
      }
      return false;
      
    }catch(error){
      return false
    }finally{
      set(()=>({activityLoader: false}));
    }
  },

  updateResponderDetails: async(details: any) => {
    set(()=>({activityLoader: true}));
    try{
      const response = await QcastApi.updateResponderDetails(details);
      if(response?.id===200){
        await clearData();
        set(() => ({
          response: [],
          recordedVideo: [],
          dbResponse: null
        }));
        return true;
      }
      return false;
      
    }catch(error){
      return false
    }finally{
      set(()=>({activityLoader: false}));
    }
  },
  saveQcast: async(qcastDetails: any)=>{
    set(()=>({activityLoader: true}));
  },
  loadVideo: async (id) => {
    return await retrieveData(id);
  },
  clearQcasts: async() => {
    await clearData();
    set(() => ({
      response: [],
      recordedVideo: [],
      thumbnails: "",
      transcription: []
    }));
  },
});

